<template>
  <div class="max-w-6xl mx-auto">
    <Head :title="'Admin - Ticket #' + ticket.id + ' - ' + ticket.subject" />

    <!-- Breadcrumb & Header -->
    <div class="mb-6">
      <div class="flex items-center gap-3 text-sm text-gray-500 mb-3">
        <Link href="/admin/tickets" class="hover:text-orange-500 transition-colors flex items-center gap-1.5 font-medium">
          <i class="fa fa-arrow-left text-xs" />
          <span>Bandeja de Tickets</span>
        </Link>
        <span class="text-gray-300 font-light">/</span>
        <span class="text-gray-900 font-medium">Ticket #{{ ticket.id }}</span>
      </div>

      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <div class="flex items-center gap-3 mb-1.5">
            <span class="font-mono text-sm font-bold bg-orange-100 text-orange-800 px-2.5 py-0.5 rounded-md">#{{ ticket.id }}</span>
            <h1 class="text-2xl font-bold text-gray-900">{{ ticket.subject }}</h1>
          </div>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
            <span><i class="fa fa-user mr-1 text-gray-400" />Cliente: <strong class="text-gray-700">{{ getUserName(ticket.user) }}</strong></span>
            <span><i class="fa fa-envelope mr-1 text-gray-400" />{{ ticket.user?.email || 'N/A' }}</span>
            <span><i class="fa fa-tag mr-1 text-gray-400" />{{ ticket.category || 'General' }}</span>
            <span>
              <i class="fa fa-flag mr-1 text-gray-400" />Prioridad: 
              <strong class="text-gray-700">{{ ticket.priority || 'Normal' }}</strong>
            </span>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <span 
            :class="{
              'bg-blue-50 text-blue-700 border-blue-200': ticket.status === 'Abierto',
              'bg-amber-50 text-amber-700 border-amber-200': ticket.status === 'En progreso',
              'bg-orange-50 text-orange-700 border-orange-200': ticket.status === 'Esperando respuesta del cliente',
              'bg-emerald-50 text-emerald-700 border-emerald-200': ticket.status === 'Resuelto',
              'bg-gray-100 text-gray-700 border-gray-200': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
            }"
            class="px-3.5 py-1.5 inline-flex text-xs font-semibold rounded-full border items-center gap-1.5 shadow-sm"
          >
            <span
              class="w-2 h-2 rounded-full" :class="{
                'bg-blue-500': ticket.status === 'Abierto',
                'bg-amber-500': ticket.status === 'En progreso',
                'bg-orange-500': ticket.status === 'Esperando respuesta del cliente',
                'bg-emerald-500': ticket.status === 'Resuelto',
                'bg-gray-400': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
              }"
            />
            {{ ticket.status }}
          </span>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      <!-- Conversación y Formulario de Respuesta (2 Cols) -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Historial de Mensajes -->
        <div class="space-y-4">
          <div 
            v-for="msg in ticket.messages" 
            :key="msg.id" 
            class="flex flex-col"
            :class="msg.user_id === ticket.user_id ? 'items-start' : 'items-end'"
          >
            <div 
              class="max-w-xl sm:max-w-2xl rounded-2xl p-5 transition-shadow"
              :class="[
                msg.user_id === ticket.user_id 
                  ? 'bg-white border border-gray-200 shadow-sm text-gray-900 rounded-tl-none' 
                  : 'bg-orange-50/70 border border-orange-200/70 text-gray-900 rounded-tr-none'
              ]"
            >
              <div class="flex items-center justify-between gap-4 mb-2 pb-2 border-b" :class="msg.user_id === ticket.user_id ? 'border-gray-100' : 'border-orange-200/50'">
                <div class="flex items-center gap-2">
                  <div 
                    class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    :class="msg.user_id === ticket.user_id ? 'bg-gray-100 text-gray-700' : 'bg-orange-200 text-orange-800'"
                  >
                    <i class="fa" :class="msg.user_id === ticket.user_id ? 'fa-user' : 'fa-headset'" />
                  </div>
                  <span class="font-semibold text-xs text-gray-900">
                    {{ getUserName(msg.user) }}
                    <span v-if="msg.user_id === ticket.user_id" class="text-gray-500 font-normal ml-1">(Cliente)</span>
                    <span v-else class="text-orange-600 font-semibold ml-1">(Soporte RealGestión)</span>
                  </span>
                </div>

                <span class="text-[11px] text-gray-400">
                  {{ formatDateTime(msg.created_at) }}
                </span>
              </div>

              <div class="text-gray-800 text-sm whitespace-pre-wrap leading-relaxed">
                {{ msg.message }}
              </div>
            </div>
          </div>
        </div>

        <!-- Formulario de Respuesta Administrativa -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fa fa-reply text-orange-500" />
              <h3 class="text-sm font-semibold text-gray-900">Responder al Cliente</h3>
            </div>
            <span class="text-xs text-gray-400">Notificación automática por correo</span>
          </div>

          <form class="p-5" @submit.prevent="submitReply">
            <div>
              <textarea 
                id="message" 
                v-model="replyForm.message" 
                rows="4" 
                class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
                placeholder="Escribe la respuesta oficial de soporte técnico aquí..."
                required
              />
              <p v-if="replyForm.errors.message" class="mt-1.5 text-xs text-red-600 font-medium">{{ replyForm.errors.message }}</p>
            </div>

            <!-- Opciones adicionales al responder -->
            <div class="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                <input 
                  v-model="markAsResolved" 
                  type="checkbox" 
                  class="rounded border-gray-300 text-orange-500 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                />
                <span class="text-xs font-medium text-gray-700">Marcar este ticket como resuelto</span>
              </label>

              <loading-button 
                :loading="replyForm.processing"
                type="submit" 
                class="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <i class="fa fa-paper-plane text-xs" />
                <span>Enviar Respuesta</span>
              </loading-button>
            </div>
          </form>
        </div>
      </div>

      <!-- Panel Lateral Informativo y Gestión (1 Col) -->
      <div class="lg:col-span-1 space-y-6">
        <!-- Control de Estado Rápido -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 class="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <i class="fa fa-sliders-h text-orange-500 text-xs" />
            <span>Gestionar Estado</span>
          </h3>

          <div class="space-y-3">
            <div>
              <label for="admin-status" class="block text-xs font-medium text-gray-600 mb-1">Estado del Ticket</label>
              <select 
                id="admin-status"
                v-model="statusForm.status" 
                class="form-select w-full border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-gray-700 bg-white text-sm py-2"
                @change="updateStatus"
              >
                <option value="Abierto">Abierto</option>
                <option value="En progreso">En progreso</option>
                <option value="Esperando respuesta del cliente">Esperando respuesta</option>
                <option value="Resuelto">Resuelto</option>
                <option value="Cerrado">Cerrado</option>
              </select>
            </div>
            <p class="text-[11px] text-gray-400">El estado se actualiza automáticamente al seleccionar.</p>
          </div>
        </div>

        <!-- Información del Cliente -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 class="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i class="fa fa-id-card text-orange-500 text-xs" />
              <span>Datos del Solicitante</span>
            </h3>
          </div>

          <div class="p-4 space-y-3 text-xs">
            <div>
              <span class="text-gray-400 block mb-0.5">Nombre completo</span>
              <span class="font-semibold text-gray-800 text-sm">{{ getUserName(ticket.user) }}</span>
            </div>
            <div>
              <span class="text-gray-400 block mb-0.5">Correo Electrónico</span>
              <a :href="'mailto:' + ticket.user?.email" class="text-orange-600 hover:underline font-medium">
                {{ ticket.user?.email || 'N/A' }}
              </a>
            </div>
            <div>
              <span class="text-gray-400 block mb-0.5">ID de Usuario</span>
              <span class="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">#{{ ticket.user?.id || ticket.user_id }}</span>
            </div>
          </div>
        </div>

        <!-- Resumen del Ticket -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 class="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i class="fa fa-info-circle text-orange-500 text-xs" />
              <span>Detalles del Ticket</span>
            </h3>
          </div>

          <div class="p-4 space-y-3 text-xs">
            <div class="flex justify-between items-center py-1 border-b border-gray-50">
              <span class="text-gray-400">Categoría</span>
              <span class="font-medium text-gray-800">{{ ticket.category || 'General' }}</span>
            </div>
            <div class="flex justify-between items-center py-1 border-b border-gray-50">
              <span class="text-gray-400">Prioridad</span>
              <span class="font-medium text-gray-800">{{ ticket.priority || 'Normal' }}</span>
            </div>
            <div class="flex justify-between items-center py-1 border-b border-gray-50">
              <span class="text-gray-400">Fecha de Creación</span>
              <span class="text-gray-700">{{ formatDateTime(ticket.created_at) }}</span>
            </div>
            <div class="flex justify-between items-center py-1">
              <span class="text-gray-400">Última Actualización</span>
              <span class="text-gray-700">{{ formatDateTime(ticket.updated_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import { useForm } from '@inertiajs/inertia-vue3'
import { ref } from 'vue'

export default {
  name: 'AdminTicketsShow',

  components: {
    Head,
    Link,
    LoadingButton,
  },
  
  layout: Layout,
  
  props: {
    ticket: Object,
  },

  setup(props) {
    const markAsResolved = ref(false)
    
    const replyForm = useForm({
      message: '',
      status: null,
    })

    const statusForm = useForm({
      status: props.ticket.status,
    })

    const submitReply = () => {
      if (markAsResolved.value) {
        replyForm.status = 'Resuelto'
      } else {
        replyForm.status = 'Esperando respuesta del cliente'
      }

      replyForm.post(`/admin/tickets/${props.ticket.id}/reply`, {
        preserveScroll: true,
        only: ['ticket'],
        onSuccess: () => {
          replyForm.reset('message')
          statusForm.status = props.ticket.status
          markAsResolved.value = false
        },
      })
    }

    const updateStatus = () => {
      statusForm.patch(`/admin/tickets/${props.ticket.id}/status`, {
        preserveScroll: true,
      })
    }

    const getUserName = (user) => {
      if (!user) return 'Usuario'
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }
      return user.name || 'Usuario'
    }

    const formatDateTime = (date) => {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-VE', { 
        dateStyle: 'medium',
        timeStyle: 'short',
        hour12: false, 
      })
    }

    return { 
      markAsResolved, 
      replyForm, 
      statusForm, 
      submitReply, 
      updateStatus,
      getUserName,
      formatDateTime,
    }
  },
}
</script>
